import React, { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { SHORT_TIME } from 'lane-shared/helpers/constants/dates';
import { doDateRangesOverlap } from 'lane-shared/helpers/dates';
import { dateFormatter } from 'lane-shared/helpers/formatters';
import { DateRangeType } from 'lane-shared/types/baseTypes/DateRangeType';
import { ReservableAvailabilityRange, ReservableAvailabilityReasonEnum } from 'lane-shared/hooks/useReservableAvailabilityByRange';
import { ChipSubLabelVariant } from '../ChipButton';

import Chips from '../Chips';

import styles from './index.scss';
import { Icon } from 'design-system-web';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import classNames from 'classnames';

const SEE_MORE_THRESHOLD = 12;
const SHOW_CAPACITY = 10;
const SHOW_CRITICAL = 3;

type ChipState = {
  subLabel: string | undefined;
  subLabelVariant: ChipSubLabelVariant;
  disabled: boolean;
};

function getChipState(
  reason: string | null,
  remainingSpots: number | null,
  t: (key: string, options?: Record<string, unknown>) => string
): ChipState {
  if (reason === ReservableAvailabilityReasonEnum.Full || remainingSpots === 0) {
    return {
      subLabel: t('web.reservable.full'),
      subLabelVariant: 'default',
      disabled: true,
    };
  }

  if (reason === ReservableAvailabilityReasonEnum.UserCapReached) {
    return {
      subLabel:
        remainingSpots != null
          ? t('web.reservable.spotsLeft', { count: remainingSpots })
          : undefined,
      subLabelVariant: 'default',
      disabled: true,
    };
  }

  if (remainingSpots != null && remainingSpots > 0 && remainingSpots < SHOW_CAPACITY) {
    return {
      subLabel: t('web.reservable.spotsLeft', { count: remainingSpots }),
      subLabelVariant: remainingSpots <= SHOW_CRITICAL ? 'critical' : 'warning',
      disabled: false,
    };
  }

  return { subLabel: undefined, subLabelVariant: 'default', disabled: false };
}

type TimeChipProps = {
  options: DateRangeType[];
  onClick: (range: DateRangeType) => void;
  value: DateRangeType;
  unavailableDateRanges?: DateRangeType[];
  timeZone?: string;
  availability?: ReservableAvailabilityRange[];
  maxQuantityPerSlot?: number;
};

export default function TimeChips({
  options,
  onClick,
  value,
  unavailableDateRanges = [],
  timeZone,
  availability = [],
  maxQuantityPerSlot,
}: TimeChipProps) {
  const { t } = useTranslation();

  const onClickHandler = (selectedValue: DateRangeType) => {
    if (doDateRangesOverlap(selectedValue, value)) {
      onClick({ startDate: null, endDate: null });
    } else {
      onClick(selectedValue);
    }
  };

   const availableTimes = useMemo(() => {
    return options
      .filter(option => {
        const isUnavailable = unavailableDateRanges.some(range =>
          doDateRangesOverlap(option, range)
        );

        if (!isUnavailable) return true;
        // When FF is on, re-include slots that the resolver marked as
        // Full/UserCapReached — they render as disabled chips instead of
        // disappearing from the list.

        if (maxQuantityPerSlot != null) {
          return availability.some(a => doDateRangesOverlap(option, a.interval));
        }

        return false;
      })
      .map(option => {
        let subLabel: string | undefined;
        let subLabelVariant: ChipSubLabelVariant = 'default';
        let disabled = false;

        if (maxQuantityPerSlot != null) {
          const match = availability.find(a =>
            doDateRangesOverlap(option, a.interval)
          );
          const reason = match?.reason ?? null;
          const remainingSpots =
            match?.remainingSpots ?? (match == null ? (maxQuantityPerSlot ?? null) : null);

          ({ subLabel, subLabelVariant, disabled } = getChipState(
            reason,
            remainingSpots,
            t
          ));
        }

        return {
          label: dateFormatter(option.startDate, SHORT_TIME, timeZone),
          value: option,
          subLabel,
          subLabelVariant,
          disabled,
        };
      });
  }, [options, unavailableDateRanges, availability, timeZone, maxQuantityPerSlot, t]);

  if (availableTimes.length === 0) {
    return (
      <div className={classNames(styles.noAvailabilityWrapper)}>
        <Icon
          name="info-circle"
          className={styles.infoIcon}
          set={ICON_SET_FONTAWESOME}
          type="far"
        />
        <p className={styles.noAvailable}>
          {t('shared.content.feature.reservable.timeSlots.noAvailability')}
        </p>
      </div>
    );
  }

  return (
    <>
      <h5 className={styles.timeChipHeader}>
        {t('web.content.feature.reservable.timeSlots.availableTimes')}
      </h5>
      <Chips<DateRangeType>
        onClick={onClickHandler}
        compare={range => doDateRangesOverlap(range, value)}
        options={availableTimes}
        itemName="time"
        hasSeeMore
        seeMoreThreshold={SEE_MORE_THRESHOLD}
        gridSize="large"
        fixedSize={maxQuantityPerSlot != null}
      />
    </>
  );
}
